'use client';
import React, { useRef } from 'react';
import Link from 'next/link';
import AppImage from '@/components/ui/AppImage';
import { ChevronLeft, ChevronRight, MapPin, Star } from 'lucide-react';

interface FeaturedItem {
  id: string;
  title: string;
  category: string;
  price: string;
  location: string;
  rating: number;
  reviews: number;
  image: string;
  alt: string;
  badge: string;
  badgeColor: string;
}

const featuredItems: FeaturedItem[] = [
{
  id: 'feat-001', title: 'Toyota Land Cruiser V8 2020', category: 'Vehicles',
  price: 'KES 8,500,000', location: 'Westlands, NBI', rating: 4.9, reviews: 24,
  image: "https://images.unsplash.com/photo-1677519498852-a86ba7aa747c",
  alt: 'White Toyota Land Cruiser V8 parked on a city street in Nairobi',
  badge: 'FEATURED', badgeColor: '#FFB300'
},
{
  id: 'feat-002', title: '3BR Apartment Karen Estate', category: 'Property',
  price: 'KES 75,000/mo', location: 'Karen, NBI', rating: 4.7, reviews: 12,
  image: "https://img.rocket.new/generatedImages/rocket_gen_img_1b6189bd6-1781267536879.png",
  alt: 'Modern three-bedroom apartment with garden view in Karen Nairobi',
  badge: 'HOT', badgeColor: '#FF3B5C'
},
{
  id: 'feat-003', title: 'iPhone 15 Pro Max 256GB', category: 'Electronics',
  price: 'KES 165,000', location: 'CBD, NBI', rating: 4.8, reviews: 56,
  image: "https://img.rocket.new/generatedImages/rocket_gen_img_1a43fe270-1765259226080.png",
  alt: 'Black iPhone 15 Pro Max displayed on white background',
  badge: 'VERIFIED', badgeColor: '#00C853'
},
{
  id: 'feat-004', title: 'Sarova Stanley Nairobi — Deluxe Room', category: 'Hotel',
  price: 'KES 12,500/night', location: 'CBD, NBI', rating: 4.6, reviews: 340,
  image: "https://img.rocket.new/generatedImages/rocket_gen_img_1a6e22c9b-1773145655512.png",
  alt: 'Luxurious hotel room with king bed and city views at Sarova Stanley',
  badge: 'TOP RATED', badgeColor: '#7C4DFF'
},
{
  id: 'feat-005', title: 'Senior Software Engineer — Safaricom PLC', category: 'Jobs',
  price: 'KES 180,000/mo', location: 'Westlands, NBI', rating: 4.9, reviews: 8,
  image: "https://img.rocket.new/generatedImages/rocket_gen_img_130d53363-1769451772821.png",
  alt: 'Modern open-plan tech office with collaborative workspaces',
  badge: 'URGENT', badgeColor: '#FF6D00'
}];


export default function FeaturedListings() {
  const scrollRef = useRef<HTMLDivElement>(null);

  const scroll = (dir: 'left' | 'right') => {
    if (scrollRef.current) {
      scrollRef.current.scrollBy({ left: dir === 'right' ? 280 : -280, behavior: 'smooth' });
    }
  };

  return (
    <div>
      <div className="flex items-center justify-between mb-3">
        <h2 className="text-base font-700 text-foreground">Featured Today</h2>
        <div className="flex items-center gap-2">
          <button onClick={() => scroll('left')} className="p-1.5 rounded-lg transition-all duration-150 hover:bg-muted active:scale-95"
          style={{ border: '1px solid var(--border)' }}>
            <ChevronLeft size={16} style={{ color: 'var(--muted-foreground)' }} />
          </button>
          <button onClick={() => scroll('right')} className="p-1.5 rounded-lg transition-all duration-150 hover:bg-muted active:scale-95"
          style={{ border: '1px solid var(--border)' }}>
            <ChevronRight size={16} style={{ color: 'var(--muted-foreground)' }} />
          </button>
        </div>
      </div>

      <div ref={scrollRef} className="flex gap-4 overflow-x-auto scrollbar-hide pb-2">
        {featuredItems.map((item) =>
        <Link key={item.id} href={`/product/${item.id}`} className="listing-card flex-shrink-0 w-64 cursor-pointer">
            <div className="relative">
              <AppImage src={item.image} alt={item.alt} width={256} height={160}
            className="w-full h-40 object-cover" />
              <span className="absolute top-2 left-2 px-2 py-0.5 rounded-full text-[10px] font-700"
            style={{ background: item.badgeColor, color: '#000' }}>
                {item.badge}
              </span>
              <span className="absolute top-2 right-2 px-2 py-0.5 rounded-full text-[10px] font-600"
            style={{ background: 'rgba(0,0,0,0.6)', color: '#fff' }}>
                {item.category}
              </span>
            </div>
            <div className="p-3">
              <p className="text-sm font-600 text-foreground truncate mb-1">{item.title}</p>
              <p className="text-base font-800 font-tabular mb-2" style={{ color: 'var(--primary)' }}>{item.price}</p>
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-1">
                  <MapPin size={11} style={{ color: 'var(--muted-foreground)' }} />
                  <span className="text-[11px]" style={{ color: 'var(--muted-foreground)' }}>{item.location}</span>
                </div>
                <div className="flex items-center gap-1">
                  <Star size={11} fill="var(--accent)" style={{ color: 'var(--accent)' }} />
                  <span className="text-[11px] font-600 font-tabular text-foreground">{item.rating}</span>
                  <span className="text-[11px]" style={{ color: 'var(--muted-foreground)' }}>({item.reviews})</span>
                </div>
              </div>
            </div>
          </Link>
        )}
      </div>
    </div>);

}